import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  ListGroup,
  ListGroupItem,
  Spinner,
} from "reactstrap";

import { copyTextToClipboard } from "../../helpers/clipboard";
import { formatCooldownCountdown } from "../../helpers/googleReserveCode";
import {
  getGoogleReserveStatus,
  GoogleReserveStatus,
  GoogleReserveSyncEvent,
  requestGoogleReserveSync,
  requestGoogleReserveUnitCode,
} from "../../services/integrationService";

interface GoogleReservePanelProps {
  unitId: number | null;
  enabled: boolean;
}

type HistoryFilter = "all" | "inbound";

const INBOUND_EVENTS = new Set([
  "CreateBooking",
  "UpdateBooking",
  "BatchAvailabilityLookup",
]);

const formatWhen = (iso: string, locale: string): string => {
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const resolveMaskedCode = (
  mapping: GoogleReserveStatus["mapping"],
  issued?: GoogleReserveStatus["issued_code"]
): string | null => {
  if (mapping?.merchant_id_masked) {
    return mapping.merchant_id_masked;
  }

  if (mapping?.merchant_id) {
    return mapping.merchant_id;
  }

  if (issued?.merchant_id_masked) {
    return issued.merchant_id_masked;
  }

  return null;
};

const hasSuccessfulSync = (history: GoogleReserveSyncEvent[]): boolean =>
  history.some(
    (item) =>
      item.ok &&
      (item.event === "booking.google.sync_availability" ||
        item.direction === "outbound")
  );

const GoogleReservePanel: React.FC<GoogleReservePanelProps> = ({
  unitId,
  enabled,
}) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [requestingCode, setRequestingCode] = useState(false);
  const [status, setStatus] = useState<GoogleReserveStatus | null>(null);
  const [countdown, setCountdown] = useState<string | null>(null);
  const [historyFilter, setHistoryFilter] = useState<HistoryFilter>("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getGoogleReserveStatus(unitId);
      setStatus(response.data);
    } catch {
      toast.error(t("studio.integrations.googleReserve.statusLoadError"));
    } finally {
      setLoading(false);
    }
  }, [t, unitId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const availableAgainAt = status?.code_request?.available_again_at;
    if (!availableAgainAt || status?.code_request?.available) {
      setCountdown(null);
      return;
    }

    const tick = () => {
      setCountdown(formatCooldownCountdown(availableAgainAt));
    };

    tick();
    const id = window.setInterval(tick, 30000);
    return () => window.clearInterval(id);
  }, [status?.code_request?.available, status?.code_request?.available_again_at]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await requestGoogleReserveSync(unitId);
      toast.success(t("studio.integrations.googleReserve.syncQueued"));
      await load();
    } catch {
      toast.error(t("studio.integrations.googleReserve.syncError"));
    } finally {
      setSyncing(false);
    }
  };

  const handleRequestCode = async () => {
    const confirmation = await Swal.fire({
      title: t("studio.integrations.googleReserve.codeRequestConfirmTitle"),
      text: t("studio.integrations.googleReserve.codeRequestConfirmBody"),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("studio.integrations.googleReserve.codeRequestConfirmYes"),
      cancelButtonText: t("studio.integrations.googleReserve.codeRequestConfirmNo"),
      confirmButtonColor: "#0ab39c",
      cancelButtonColor: "#878a99",
      focusCancel: true,
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    setRequestingCode(true);
    try {
      const response = await requestGoogleReserveUnitCode(unitId);
      const ticketNumber = response.data?.ticket_public_number;
      toast.success(
        ticketNumber
          ? t("studio.integrations.googleReserve.codeRequestSuccessWithTicket", {
              number: ticketNumber,
            })
          : t("studio.integrations.googleReserve.codeRequestSuccess")
      );
      await load();
    } catch {
      toast.error(t("studio.integrations.googleReserve.codeRequestError"));
    } finally {
      setRequestingCode(false);
    }
  };

  const handleCopyMasked = async (masked: string) => {
    const ok = await copyTextToClipboard(masked);
    if (ok) {
      toast.success(t("studio.integrations.googleReserve.maskedCopySuccess"));
    } else {
      toast.error(t("studio.integrations.googleReserve.maskedCopyError"));
    }
  };

  const filteredHistory = useMemo(() => {
    if (!status) {
      return [];
    }

    if (historyFilter === "all") {
      return status.sync_history;
    }

    return status.sync_history.filter(
      (item) =>
        item.direction === "inbound" || INBOUND_EVENTS.has(item.event)
    );
  }, [historyFilter, status]);

  if (loading) {
    return (
      <div className="text-center py-3">
        <Spinner size="sm" color="primary" />
      </div>
    );
  }

  if (!status) {
    return null;
  }

  const environmentLabel =
    status.environment === "production"
      ? t("studio.integrations.googleReserve.env.production")
      : t("studio.integrations.googleReserve.env.sandbox");

  const maskedCode = resolveMaskedCode(status.mapping, status.issued_code);
  const hasUnitCode =
    status.mapping?.has_unit_code === true || Boolean(status.mapping?.merchant_id_masked);
  const preparationActive =
    status.preparation?.active !== false &&
    (status.environment !== "production" || !status.platform_configured);
  const etaDays = status.preparation?.eta_business_days ?? 10;
  const codeRequestAvailable = status.code_request?.available !== false;
  const issuedAvailable = status.issued_code?.available === true;
  const productionReady = status.readiness?.production_ready === true;
  const sandboxReviewReady =
    !productionReady && status.readiness?.sandbox_review_ready === true;
  const hoursSynced = hasSuccessfulSync(status.sync_history);
  const googleReleaseReady = productionReady || status.environment === "production";

  const checklist = [
    {
      key: "connection",
      ok: status.platform_configured,
      label: t("studio.integrations.googleReserve.checklist.connection"),
    },
    {
      key: "unitCode",
      ok: hasUnitCode,
      label: t("studio.integrations.googleReserve.checklist.unitCode"),
    },
    {
      key: "hours",
      ok: hoursSynced,
      label: t("studio.integrations.googleReserve.checklist.hours"),
    },
    {
      key: "release",
      ok: googleReleaseReady,
      label: t("studio.integrations.googleReserve.checklist.release"),
    },
  ];

  return (
    <div className="mt-3">
      <hr className="my-3" />
      <h6 className="mb-3">{t("studio.integrations.googleReserve.panelTitle")}</h6>

      <div className="d-flex flex-wrap gap-2 mb-3">
        <Badge color={status.environment === "production" ? "success" : "warning"}>
          {environmentLabel}
        </Badge>
        <Badge color={status.platform_configured ? "info" : "secondary"}>
          {status.platform_configured
            ? t("studio.integrations.googleReserve.platformReady")
            : t("studio.integrations.googleReserve.platformPending")}
        </Badge>
        {productionReady && (
          <Badge color="success">
            {t("studio.integrations.googleReserve.productionReadyBadge")}
          </Badge>
        )}
        {sandboxReviewReady && (
          <Badge color="primary">
            {t("studio.integrations.googleReserve.sandboxReviewReadyBadge")}
          </Badge>
        )}
        <Badge color={enabled ? "success" : "secondary"}>
          {enabled
            ? t("studio.integrations.status.enabled")
            : t("studio.integrations.status.disabled")}
        </Badge>
      </div>

      <div className="border rounded p-3 mb-3 bg-light">
        <div className="fw-semibold mb-2">
          {t("studio.integrations.googleReserve.checklistTitle")}
        </div>
        <ul className="list-unstyled mb-0 small">
          {checklist.map((item) => (
            <li
              key={item.key}
              className="d-flex align-items-center gap-2 mb-1"
            >
              <i
                className={
                  item.ok
                    ? "ri-checkbox-circle-fill text-success"
                    : "ri-checkbox-blank-circle-line text-muted"
                }
                aria-hidden
              />
              <span className={item.ok ? "text-body" : "text-muted"}>
                {item.label}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {preparationActive && (
        <Alert color="warning" className="py-2 small">
          <div className="fw-semibold mb-1">
            {t("studio.integrations.googleReserve.preparationTitle")}
          </div>
          <div>
            {t("studio.integrations.googleReserve.preparationEta", {
              days: etaDays,
            })}
          </div>
          {!status.platform_configured && (
            <div className="mt-1">
              {t("studio.integrations.googleReserve.platformPendingHelp")}
            </div>
          )}
        </Alert>
      )}

      {issuedAvailable && !hasUnitCode && (
        <Alert color="success" className="py-2 small">
          {t("studio.integrations.googleReserve.issuedCodeReady", {
            code: status.issued_code?.merchant_id_masked ?? "••••",
          })}
        </Alert>
      )}

      {!hasUnitCode && (
        <div className="border rounded p-3 mb-3 text-center bg-light">
          <div className="fw-semibold mb-1">
            {t("studio.integrations.googleReserve.codeEmptyTitle")}
          </div>
          <p className="text-muted small mb-3">
            {t("studio.integrations.googleReserve.codeEmptyBody")}
          </p>
          <Button
            color="primary"
            size="sm"
            disabled={!codeRequestAvailable || requestingCode}
            onClick={() => void handleRequestCode()}
          >
            {requestingCode ? (
              <Spinner size="sm" />
            ) : (
              t("studio.integrations.googleReserve.codeRequestCta")
            )}
          </Button>
          {!codeRequestAvailable && (
            <p className="text-muted small mb-0 mt-2">
              {countdown
                ? t("studio.integrations.googleReserve.codeRequestCooldownCountdown", {
                    remaining: countdown,
                  })
                : t("studio.integrations.googleReserve.codeRequestCooldown")}
            </p>
          )}
        </div>
      )}

      {status.mapping && (hasUnitCode || issuedAvailable) && maskedCode && (
        <div className="border rounded p-3 mb-3 bg-light">
          <div className="fw-semibold mb-2">
            {t("studio.integrations.googleReserve.mappingTitle")}
          </div>
          <div className="d-flex align-items-center flex-wrap gap-2 small">
            <span className="badge bg-primary-subtle text-primary">
              {status.mapping.unit_name ??
                t("studio.integrations.googleReserve.unitUnknown")}
            </span>
            <i className="ri-arrow-right-line" aria-hidden />
            <span className="badge bg-dark-subtle text-dark font-monospace">
              {t("studio.integrations.googleReserve.merchantLabel", {
                id: maskedCode,
              })}
            </span>
            <Button
              color="soft-secondary"
              size="sm"
              type="button"
              onClick={() => void handleCopyMasked(maskedCode)}
            >
              {t("studio.integrations.googleReserve.maskedCopyCta")}
            </Button>
          </div>
          <p className="text-muted small mb-0 mt-2">
            {t("studio.integrations.googleReserve.maskedCodeHelp")}
          </p>
        </div>
      )}

      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <h6 className="mb-0">
          {t("studio.integrations.googleReserve.historyTitle")}
        </h6>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <ButtonGroup size="sm">
            <Button
              color={historyFilter === "all" ? "primary" : "light"}
              outline={historyFilter !== "all"}
              type="button"
              onClick={() => setHistoryFilter("all")}
            >
              {t("studio.integrations.googleReserve.historyFilterAll")}
            </Button>
            <Button
              color={historyFilter === "inbound" ? "primary" : "light"}
              outline={historyFilter !== "inbound"}
              type="button"
              onClick={() => setHistoryFilter("inbound")}
            >
              {t("studio.integrations.googleReserve.historyFilterInbound")}
            </Button>
          </ButtonGroup>
          <Button
            color="soft-primary"
            size="sm"
            disabled={!enabled || syncing || !hasUnitCode}
            onClick={() => void handleSync()}
          >
            {syncing ? (
              <Spinner size="sm" />
            ) : (
              t("studio.integrations.googleReserve.syncNow")
            )}
          </Button>
        </div>
      </div>

      {filteredHistory.length === 0 ? (
        <p className="text-muted small mb-0">
          {historyFilter === "inbound"
            ? t("studio.integrations.googleReserve.historyEmptyInbound")
            : t("studio.integrations.googleReserve.historyEmpty")}
        </p>
      ) : (
        <ListGroup flush className="border rounded">
          {filteredHistory.map((item) => (
            <ListGroupItem
              key={`${item.at}-${item.event}-${item.direction}`}
              className="d-flex justify-content-between align-items-start gap-2 py-2"
            >
              <div>
                <div className="small">{item.summary}</div>
                <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                  {formatWhen(item.at, i18n.language)}
                </div>
              </div>
              <Badge color={item.ok ? "success" : "danger"} pill>
                {item.ok
                  ? t("studio.integrations.googleReserve.historyOk")
                  : t("studio.integrations.googleReserve.historyFail")}
              </Badge>
            </ListGroupItem>
          ))}
        </ListGroup>
      )}
    </div>
  );
};

export default GoogleReservePanel;
